import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen = ({ message = "Just a sec" }) => {
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    const currentTheme = document.documentElement.getAttribute('data-theme') || localStorage.getItem('vjconnect_theme') || 'dark';
    setTheme(currentTheme);

    // Optional: Listen for theme changes if needed
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          setTheme(document.documentElement.getAttribute('data-theme'));
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const isLight = theme === 'light';

  // --- Advanced Expression Engine (God-Like Sync) ---

  // 1. Deep Synchronized Breathing
  const bodyVariants = {
    animate: {
      scaleY: [1, 1.15, 1.05, 1.15, 1], // Heart-beat style double inhale
      scaleX: [1, 0.94, 1.02, 0.94, 1],
      y: [0, -12, -4, -12, 0],
      filter: [
        "drop-shadow(0 10px 15px rgba(0,0,0,0.1))",
        "drop-shadow(0 20px 30px rgba(255,255,255,0.2))", // Glow on peak
        "drop-shadow(0 10px 15px rgba(0,0,0,0.1))"
      ],
      transition: {
        duration: 5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // 2. Interactive Looking (Pupils)
  const pupilVariants = {
    animate: (i) => ({
      x: [0, 5, -5, 0, 0, i % 2 === 0 ? 4 : -4, 0], // Look left/right randomly
      y: [0, -2, 2, 0, 0, 0, 0],
      transition: {
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
        delay: i * 0.5
      }
    })
  };

  // 3. Eye Squinting
  const eyeVariants = {
    animate: {
      scaleY: [1, 0.4, 1, 1, 0.4, 1], // Blink + Squint
      transition: {
        duration: 5,
        repeat: Infinity,
        times: [0, 0.4, 0.5, 0.9, 0.95, 1]
      }
    }
  };

  // 4. Laughing/Talking Mouths
  const mouthVariants = {
    animate: (type) => {
      const frames = type === 'orange' ?
        { height: [12, 20, 10, 20, 12], width: [34, 40, 34, 40, 34] } :
        { scaleY: [1, 1.8, 0.8, 1.8, 1], scaleX: [1, 1.2, 1, 1.2, 1] };

      return {
        ...frames,
        transition: {
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut"
        }
      };
    }
  };

  const textVariants = {
    animate: {
      opacity: [0.4, 1, 0.4],
      scale: [0.95, 1.05, 0.95],
      textShadow: [
        "0 0 0px rgba(255,255,255,0)",
        "0 0 20px rgba(255,255,255,0.5)",
        "0 0 0px rgba(255,255,255,0)"
      ],
      transition: {
        duration: 5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className={`loading-overlay ${theme}`}>
      <style>
        {`
          .loading-overlay {
            position: fixed;
            inset: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            font-family: 'Inter', sans-serif;
            overflow: hidden;
            transition: background 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .loading-overlay.dark {
            background: linear-gradient(135deg, #050510 0%, #101025 50%, #0a0a1a 100%);
            color: white;
          }

          .loading-overlay.light {
            background: linear-gradient(120deg, #f8fafc 0%, #e2e8f0 100%);
            color: #0f172a;
          }

          .loading-overlay::before {
            content: '';
            position: absolute;
            inset: 0;
            background-image:
              radial-gradient(1.5px 1.5px at 10% 20%, var(--star-color, #fff) 0%, transparent 50%),
              radial-gradient(1.5px 1.5px at 30% 50%, var(--star-color, #fff) 0%, transparent 50%),
              radial-gradient(1.5px 1.5px at 70% 30%, var(--star-color, #fff) 0%, transparent 50%),
              radial-gradient(1.5px 1.5px at 90% 80%, var(--star-color, #fff) 0%, transparent 50%);
            background-repeat: repeat;
            background-size: 300px 300px;
            opacity: var(--star-opacity, 0.4);
            animation: stars-pulse 4s infinite alternate;
          }

          @keyframes stars-pulse {
            from { opacity: var(--star-opacity, 0.2); transform: scale(1); }
            to { opacity: calc(var(--star-opacity, 0.2) * 2); transform: scale(1.05); }
          }

          .loading-overlay.dark { --star-color: #fff; --star-opacity: 0.15; }
          .loading-overlay.light { --star-color: #64748b; --star-opacity: 0.1; }

          .mascot-container {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            gap: 25px;
            margin-bottom: 50px;
            position: relative;
            perspective: 1200px;
          }

          .mascot {
            position: relative;
            transform-origin: bottom center;
            transition: filter 0.3s ease;
          }

          /* Mascot base colors updated for premium look */
          .mascot-orange { width: 150px; height: 75px; background: #FF6B35; border-radius: 150px 150px 0 0; }
          .mascot-purple { width: 85px; height: 170px; background: #6366f1; border-radius: 16px; }
          .mascot-black  { width: 70px; height: 140px; background: #1e1e2e; border-radius: 14px; }
          .mascot-yellow { width: 80px; height: 130px; background: #facc15; border-radius: 16px; }

          .eye {
            position: absolute;
            width: 16px;
            height: 16px;
            background: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
          }

          .pupil {
            width: 8px;
            height: 8px;
            background: #0f172a;
            border-radius: 50%;
          }

          /* Positioning - centered and expressive */
          .mascot-orange .eye.left { left: 40px; top: 20px; }
          .mascot-orange .eye.right { right: 40px; top: 20px; }
          .mascot-purple .eye.left { left: 20px; top: 30px; }
          .mascot-purple .eye.right { right: 20px; top: 30px; }
          .mascot-black .eye.left { left: 16px; top: 24px; }
          .mascot-black .eye.right { right: 16px; top: 24px; }
          .mascot-yellow .eye { left: 50%; top: 28px; transform: translateX(-50%); }

          .mouth {
            position: absolute;
            background: #0f172a;
            border-radius: 20px;
            opacity: 0.9;
            left: 50%;
            transform: translateX(-50%);
          }

          .mascot-orange .mouth {
            width: 38px;
            height: 14px;
            border-radius: 0 0 19px 19px;
            bottom: 12px;
            border: 2.5px solid #312e81;
            background: transparent;
            border-top: 0;
          }

          .mascot-purple .mouth {
            width: 26px;
            height: 10px;
            border-radius: 0 0 13px 13px;
            bottom: 24px;
            border: 2px solid #312e81;
            background: transparent;
            border-top: 0;
          }

          .mascot-black .mouth {
            width: 20px;
            height: 4px;
            bottom: 22px;
            background: #000;
          }

          .mascot-yellow .mouth {
            width: 24px;
            height: 8px;
            border-radius: 0 0 12px 12px;
            bottom: 28px;
            border: 2px solid #312e81;
            background: transparent;
            border-top: 0;
          }

          .loading-text-main {
            font-size: 3.2rem;
            font-weight: 950;
            margin-bottom: 20px;
            letter-spacing: -2px;
            text-transform: uppercase;
            text-align: center;
            /* Use standard color as fallback for visibility */
            color: #ffffff;
            background: linear-gradient(to bottom, #ffffff 20%, #cbd5e1 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
          }

          .light .loading-text-main {
            color: #1e293b;
            background: linear-gradient(to bottom, #0f172a 20%, #475569 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            filter: none;
          }

          .loading-text-sub {
            font-size: 1.6rem;
            font-weight: 700;
            letter-spacing: 2px;
            text-transform: uppercase;
            background: linear-gradient(to right, #6366f1, #a855f7);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            text-align: center;
          }

          @media (max-width: 768px) {
            .mascot-black, .mascot-yellow { display: none; }
            .mascot-container { gap: 20px; }
            .loading-text-main { font-size: 2.2rem; }
            .loading-text-sub { font-size: 1.2rem; }
          }
        `}
      </style>

      <div className="mascot-container">
        {/* Black Mascot (The Stoic) */}
        <motion.div className="mascot mascot-black" variants={bodyVariants} animate="animate">
          <motion.div className="eye left" variants={eyeVariants} animate="animate">
            <motion.div className="pupil" variants={pupilVariants} custom={0} animate="animate" />
          </motion.div>
          <motion.div className="eye right" variants={eyeVariants} animate="animate">
            <motion.div className="pupil" variants={pupilVariants} custom={1} animate="animate" />
          </motion.div>
          <motion.div className="mouth" variants={mouthVariants} custom="black" animate="animate" />
        </motion.div>

        {/* Orange Mascot (The Leader) */}
        <motion.div className="mascot mascot-orange" variants={bodyVariants} animate="animate">
          <motion.div className="eye left" variants={eyeVariants} animate="animate">
            <motion.div className="pupil" variants={pupilVariants} custom={2} animate="animate" />
          </motion.div>
          <motion.div className="eye right" variants={eyeVariants} animate="animate">
            <motion.div className="pupil" variants={pupilVariants} custom={3} animate="animate" />
          </motion.div>
          <motion.div className="mouth" variants={mouthVariants} custom="orange" animate="animate" />
        </motion.div>

        {/* Purple Mascot (The Tall One) */}
        <motion.div className="mascot mascot-purple" variants={bodyVariants} animate="animate">
          <motion.div className="eye left" variants={eyeVariants} animate="animate">
            <motion.div className="pupil" variants={pupilVariants} custom={4} animate="animate" />
          </motion.div>
          <motion.div className="eye right" variants={eyeVariants} animate="animate">
            <motion.div className="pupil" variants={pupilVariants} custom={5} animate="animate" />
          </motion.div>
          <motion.div className="mouth" variants={mouthVariants} custom="purple" animate="animate" />
        </motion.div>

        {/* Yellow Mascot (The Watcher) */}
        <motion.div className="mascot mascot-yellow" variants={bodyVariants} animate="animate">
          <motion.div className="eye" variants={eyeVariants} animate="animate">
            <motion.div className="pupil" variants={pupilVariants} custom={6} animate="animate" />
          </motion.div>
          <motion.div className="mouth" variants={mouthVariants} custom="yellow" animate="animate" />
        </motion.div>
      </div>

      <motion.h1
        className="loading-text-main"
        initial={{ filter: "blur(10px)", opacity: 0, y: 20 }}
        animate={{ filter: "blur(0px)", opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        {message}
      </motion.h1>

      <motion.p
        className="loading-text-sub"
        variants={textVariants}
        animate="animate"
      >
        Take a breath...
      </motion.p>
    </div>
  );
};

export default LoadingScreen;
